// src/ui/TextPostStyler.jsx — the controls for a styled text post, and the card
// that renders one.
//
// The editor is deliberately WYSIWYG: you type ON the background at the real
// size, with the real weight, rather than picking options next to a plain box
// and finding out what you made after you post it.

import React from 'react';
import {
  BACKGROUNDS, DOODLES, SIZES, doodleUrl, textPostCss,
} from '../services/textPost';

/** The swatch strip + emphasis toggles that sit under the composer. */
export function TextStyleBar({ style, onChange, disabled }) {
  const set = (patch) => onChange({ ...style, ...patch });

  return (
    <div className="tps" aria-label="Text post style">
      <div className="tps-row">
        <span className="tps-label">Background</span>
        <div className="tps-swatches">
          {BACKGROUNDS.map((b) => {
            const on = style.bg === b.id;
            return (
              <button
                key={b.id} type="button" disabled={disabled}
                className={`tps-sw ${on ? 'on' : ''} ${b.id === 'plain' ? 'none' : ''}`}
                style={b.css ? { background: b.css, '--sw-ink': b.ink || 'var(--text)' } : undefined}
                title={b.name} aria-label={b.name} aria-pressed={on}
                onClick={() => set({ bg: b.id })}
              >
                {/* 🟢 A TICK, NOT ONLY A RING — at a glance a ring on one of nine
                    round swatches is not an answer to "which one is on?". It is
                    drawn in the background's OWN ink so it reads on every ramp. */}
                {on && (
                  <svg className="tps-tick" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tps-row">
        <span className="tps-label">Doodles</span>
        <div className="tps-chips">
          {DOODLES.map((d) => (
            <button
              key={d.id || 'none'} type="button" disabled={disabled}
              className={`tps-chip ${(style.doodle || null) === d.id ? 'on' : ''}`}
              onClick={() => set({ doodle: d.id })}
            >
              {/* 🟥 A PREVIEW MUST BE LEGIBLE WHERE IT IS DRAWN, not where the
                  thing it previews will end up. This used to paint the pattern
                  in `bgById(style.bg).ink` — the CARD's contrast colour, which
                  is #ffffff for six of the eight gradients — onto a pale chip.
                  White on white; and at the markup's own 0.3–0.5 wallpaper
                  alpha even where it did contrast.
                  🟢 A MASK, NOT AN IMAGE. The shape comes from the SVG and the
                     COLOUR from `currentColor`, so the chip's own text colour
                     paints it — correct in both themes, and correct again when
                     the chip is selected and inverts, with nothing to keep in
                     step by hand. */}
              {d.id && (
                <i className="tps-dood" style={{ '--dood': doodleUrl(d.id, '#000', 1) }} />
              )}
              {d.name}
            </button>
          ))}
        </div>
      </div>

      <div className="tps-row">
        <span className="tps-label">Text</span>
        <div className="tps-chips">
          <button
            type="button" disabled={disabled}
            className={`tps-chip mono ${style.bold ? 'on' : ''}`}
            title="Bold" aria-pressed={style.bold}
            onClick={() => set({ bold: !style.bold })}
          >
            <b>B</b>
          </button>
          <button
            type="button" disabled={disabled}
            className={`tps-chip mono ${style.underline ? 'on' : ''}`}
            title="Underline" aria-pressed={style.underline}
            onClick={() => set({ underline: !style.underline })}
          >
            <u>U</u>
          </button>
          <span className="tps-gap" />
          {SIZES.map((sz) => (
            <button
              key={sz.id} type="button" disabled={disabled}
              className={`tps-chip ${style.size === sz.id ? 'on' : ''}`}
              onClick={() => set({ size: sz.id })}
            >
              {sz.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The card, used both as the composer's live canvas and as the feed's renderer.
 *
 * 🟢 ONE COMPONENT FOR BOTH, on purpose. When the preview and the post are drawn
 *    by different code they drift, and the drift is only ever discovered after
 *    something has been published. Passing `editable` swaps the text node for a
 *    textarea and changes nothing else.
 */
export function TextPostCard({
  style, text, editable = false, value, onChange, placeholder, disabled,
}) {
  const css = textPostCss(style);
  /* 🟥 AN UNTOUCHED DRAFT IS NOT A POSTER. The card carries the post's own font
     size inline, which is right for something wearing a background and wrong
     for the empty composer — "Share a vibe…" was rendering at 26px, centred, in
     a 120px box, which made an empty composer the loudest thing on the page.
     When the style is `quiet` the size and weight are simply not emitted, and
     the stylesheet gives it ordinary composer proportions.
     🟢 THIS CAN ONLY EVER AFFECT THE COMPOSER: `quiet` is exactly
        `!isStyledText`, and the feed does not draw this card at all for a post
        that is not styled. */
  const textCss = css.quiet
    ? { ...css.text, fontSize: undefined, fontWeight: undefined }
    : css.text;
  return (
    /* 🟥 THE BACKGROUND IS A LAYER, NOT THE ELEMENT'S OWN. A CSS `transition`
       cannot interpolate between two background IMAGES — a gradient appearing or
       changing snaps, whatever duration is written — so the card that was
       supposed to ease into place arrived in one frame. An overlay whose OPACITY
       transitions does what the transition was asked for, and the colour still
       carries on the wrapper so the text stays legible throughout. */
    <div
      className={`tpc ${css.plain ? 'plain' : ''} ${css.quiet ? 'quiet' : ''} ${editable ? 'edit' : ''}`}
      style={{ color: css.wrap.color }}
    >
      <span className="tpc-bg" aria-hidden="true" style={{ background: css.wrap.background }} />
      {editable ? (
        <textarea
          className="tpc-input" style={textCss} value={value} placeholder={placeholder}
          disabled={disabled} rows={1}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <p className="tpc-text" style={textCss}>{text}</p>
      )}
    </div>
  );
}
